<template>
	<div id="project-selector-modal" class="hidden modal-new">
		<div class="modal-header-container">
			<v-menu offset-y :content-class="'account-menu--theme --projects'">
				<template v-slot:activator="{ on, attrs }">
					<div class="organization-selector" v-bind="attrs" v-on="on" :class="{'--unread-notifications': notificationsOfNonActiveOrganization.length > 0}">
						<span class="organization-selector--name">{{ account.organization.name }} Projects</span>
						<i class="material-icons">arrow_drop_down</i>
					</div>
				</template>

				<div class="account-menu-content">
					<span>My Organisations</span>
					<AccountSwitch />

					<v-list>
						<v-list-item>
							<AccountPanel />
						</v-list-item>
						<v-list-item>
							<Logout />
						</v-list-item>
					</v-list>
				</div>
			</v-menu>

            <Notifications />

			<v-text-field
				v-model="searchProject"
				:id="'project-search-input'"
				:class="'search-container'"
				append-icon="search"
				label="Search projects"
				dark
				flat
				solo-inverted
				hide-details
				clearable
				clear-icon="mdi-close-circle-outline"
				@input="resetPage"
			></v-text-field>
			<btnClose v-if="project.id && UILoadedStatus"></btnClose>
		</div>

		<div class="modal-tools-container">
			<div class="view-buttons-coantainer">
				<i
					@click="listView = false"
					class="material-icons"
					:class="{ '--active': !listView, '--disabled': noAssignedProject }"
					content="Grid View"
					v-tippy
				>
					view_module
				</i>
				<i
					@click="listView = true"
					class="material-icons"
					:class="{ '--active': listView, '--disabled': noAssignedProject }"
					content="List View"
					v-tippy
				>
					view_list
				</i>
			</div>
		</div>

		<div v-if="noAssignedProject" class="modal-body-container --no-projects-assigned">
			<i class="material-icons">broken_image</i>
			<p>
				No projects yet.
				<br />
				Please ask admin to assign you to a project.
			</p>
		</div>

		<div v-else class="modal-body-container">
			<div v-if="!listView" class="projects-container">
				<div
					v-for="project in filteredProjectsWithPlaceholders"
					:key="project.id"
					class="project"
					@click="onProjectSelection(project.id)"
					@dblclick="onProjectSelectionDoubleClick(project.id)"
					:class="{
						'--selected': selectedProject == project.id,
						'--loading': loadingProject == project.id,
						'--placeholder': project.placeholder,
					}"
				>
					<div class="project-image-container" :class="{ '--default': isDefaultProjectImage(project.id) }">
						<img class="project-image" :src="projectsFeaturedImage(project.id)" />
						<img
							v-if="project.owner && user.id !== project.owner.id"
							:src="getGravatar(project.owner.email)"
							class="project-owner-avatar"
							alt=""
						/>

						<div class="spinner-container" v-if="project.id == loadingProject">
							<v-progress-circular indeterminate color="var(--primary)" size="30" width="3"></v-progress-circular>
						</div>
					</div>

					<span v-if="project.id !== renamingProjectId" class="project-name">{{ project.name }}</span>
					<v-text-field
						v-else
						@keydown="handleProjectRenaming"
						@blur="cancelRenaming"
						v-model="renamingProject"
						autofocus
						label="Project Name"
						dark
						flat
						solo-inverted
						hide-details
					></v-text-field>

					<v-menu v-if="!isGuest" :content-class="'project-options-container'" offset-y>
						<template v-slot:activator="{ on, attrs }">
							<i class="material-icons project-options-btn" :id="project.id" v-bind="attrs" v-on="on" @click="openSettings(project.id)">
								more_vert
							</i>
						</template>
						<v-list>
							<v-list-item @click="onRename(project)" :disabled="!canRenameProject">
								<span class="option">Rename</span>
							</v-list-item>
							<v-list-item @click="onDelete" :disabled="!canDeleteProject">
								<span class="option">Delete</span>
							</v-list-item>
							<v-list-item v-if="canAssignUsersToProject(project)" @click="onAssignUsers">
								<span class="option">Manage Users and Guests</span>
							</v-list-item>
							<v-list-item v-else @click="onShowMembers" :disabled="isGuest">
								<span class="option">Project Members</span>
							</v-list-item>
						</v-list>
					</v-menu>
				</div>
			</div>

			<div v-else class="projects-container--list-view">
				<v-virtual-scroll :items="projectsListView" :height="listViewHeight" item-height="70" @scroll.native="onScroll">
					<template v-slot="{ item }">
						<div
							class="project"
							@click="onProjectSelection(item.id)"
							@dblclick="onProjectSelectionDoubleClick(item.id)"
							:class="{
							    '--selected': selectedProject == item.id,
							    '--loading': item.id == loadingProject,
						        '--is-guest': isGuest,
							}"
						>
							<div class="project-image-container">
								<img :src="projectsFeaturedImage(item.id)" class="project-image" :class="{ '--default': isDefaultProjectImage(item.id) }" />

								<div class="spinner-container" v-if="item.id == loadingProject">
									<v-progress-circular indeterminate color="var(--primary)" size="25" width="3"></v-progress-circular>
								</div>
							</div>

							<span v-if="item.id !== renamingProjectId" class="project-name">{{ item.name }}</span>
							<v-text-field
								v-else
								@keydown="handleProjectRenaming"
								@blur="cancelRenaming"
								v-model="renamingProject"
								autofocus
								label="Project Name"
								dark
								flat
								solo-inverted
								hide-details
							></v-text-field>

							<div class="project-owner-container" v-if="item.owner">
								<img :src="getGravatar(item.owner.email)" class="project-owner-avatar" alt="" />
                                <span class="project-owner-name">{{ item.owner.name }} ({{
                                        ownerRole(item.owner)
                                    }})</span>
                            </div>

							<span class="project-date" v-if="item.owner">
                                {{ Moment(item.created_at).format("MMMM D, YYYY") }}
                            </span>

							<v-menu v-if="!isGuest" :content-class="'project-options-container'" offset-y>
								<template v-slot:activator="{ on, attrs }">
									<i class="material-icons project-options-btn" :id="item.id" v-bind="attrs" v-on="on" @click="openSettings(item.id)">
										more_vert
									</i>
								</template>
								<v-list>
									<v-list-item @click="onRename(item)" :disabled="!canRenameProject">
										<span class="option">Rename</span>
									</v-list-item>
									<v-list-item @click="onDelete" :disabled="!canDeleteProject">
										<span class="option">Delete</span>
									</v-list-item>
									<v-list-item v-if="canAssignUsersToProject(project)" @click="onAssignUsers">
										<span class="option">Manage Users and Guests</span>
									</v-list-item>
									<v-list-item v-else @click="onShowMembers" :disabled="isGuest">
										<span class="option">Project Members</span>
									</v-list-item>
								</v-list>
							</v-menu>
						</div>
					</template>
				</v-virtual-scroll>
			</div>

			<v-pagination
				v-if="projects && projects.length > projectsPerPage && totalPages > 1"
				v-model="page"
				:length="totalPages"
				total-visible="7"
				circle
				id="projects-pagination"
				:style="{ opacity: listView ? '0' : '1' }"
				@input="onPageChange"
			></v-pagination>
		</div>

		<div class="buttons-continer" :class="{'--single-item': isGuest}">
			<button type="button" class="modal-btn" :class="{ '--disabled': loadingProject }" @click="openProject" :disabled="!selectedProject">
				<v-progress-circular v-if="loadingProject" indeterminate color="var(--primary)" size="18" width="3"></v-progress-circular>
				<span v-else>Open</span>
			</button>

			<button
                v-if="!isGuest"
				type="button"
				class="modal-btn --secondary"
				:class="{ '--disabled': loadingProject || !canCreateProject }"
				@click="createProject"
				name="new-project"
			>
				New Project
				<tippy v-if="!canCreateProject" theme="" to="new-project">
                    Please upgrade your plan to unlock unlimited projects
				</tippy>
			</button>
		</div>
	</div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import LoadScripts from "@/mixins/LoadScripts";
import AvatarMixins from "@/mixins/Avatar";
import CanMixin from "@/mixins/CanMixin";

import AccountSwitch from "@/components/account-menu/Switch";
import Logout from "@/components/account-menu/Logout";
import AccountPanel from "@/components/account-menu/AccountPanel";
import btnClose from "@/components/app/buttons/modal/Close";
import Notifications from "@/components/app/notifications/Notifications";
import { detectSafari } from "@/assets/js/Utilities";
import Cookies from 'js-cookie';

export default {
	mixins: [LoadScripts, AvatarMixins, CanMixin],
	components: {
		AccountSwitch,
		Logout,
		AccountPanel,
		btnClose,
        Notifications,
	},

	data() {
		return {
			loadingProject: null,
			searchProject: "",
			listView: false,
			listViewHeight: null,
			page: 1,
			projectsPerPage: 9,
			filteredProjectsLength: null,
			renamingProjectId: undefined,
			renamingProject: "",
		};
	},

    created() {
        this.$root.projectsModal = this;
    },

    async mounted() {
        const response = await this.$store.dispatch("Projects/fetchProjects");
		if (response.status == 401) {
			await this.$store.dispatch('Auth/signout', false);
            this.$router.push({ name: "login" });
            window.vm.$snackbar("You have been automatically logged out because your session has expired. Please log back in.", "error");
            return;
		} else if (response?.data?.errors?.length) {
            document.querySelector("#error-while-fetching-projects-modal").classList.toggle("hidden");
            document.querySelector("#error-while-fetching-projects-modal").classList.toggle("fadeInDownModal");
            return;
		}

		this.$store.dispatch("Organizations/fetchOrganization");

        this.$store.dispatch('Notifications/fetch');
        this.$store.dispatch("Projects/setProjectsFeaturedImage", this.getProjectsThumbsView());

		if (this.canCreateProject && this.account.role_id == 2 && this.projects.length == 0) {
			document.querySelector("#modal-create-project").classList.toggle("hidden");
			document.querySelector("#modal-create-project").classList.toggle("fadeInDownModal");
		} else {
			document.querySelector("#project-selector-modal").classList.toggle("hidden");
			document.querySelector("#project-selector-modal").classList.toggle("fadeInDownModal");

			if (this.projects.length > 0) {
				document.querySelector("#project-selector-modal .project").click();
			}
		}

		if (this.projects.length > 0) {
			this.listViewHeight = Math.round(document.querySelector(".projects-container").offsetHeight);
		}

		this.$root.$on("setProjectsFeaturedImage", () => {
			this.$store.dispatch("Projects/setProjectsFeaturedImage", this.getProjectsThumbsView());
		});

		// not for [member, guest]
		if (this.$store.getters['User/isMemberOrLower']) return;

		await this.$store.dispatch("User/fetchCurrentPlan");

		if (this.currentPlan?.fastspring.state == "overdue" && this.currentPlan.id !== 1) {
			this.$snackbar(`Your last payment failed. Please <a href="/account/billing" style="text-decoration: underline; color: white">update billing details</a> to keep your subscription and files.`, "error", {
				timeout: -1,
			});
		}

        if (this.account.plan_id == 1) {
            vm.$store.dispatch('UI/displayFreePlanLimitationMessages');

            // let message = "Black Friday – 30% OFF + FREE 🔥 Lutify.me Pro Package by subscribing to ANNUAL plan. Coupon: BF202130OFF. <a href=\"account/change-plan\" style=\"text-decoration: underline; color: white\">Upgrade now.</a>";
            //
            // this.$snackbar(message, "error", {
            //     class: 'black-friday-deal',
            //     timeout: -1,
            //     clickHandler() {
            //         vm.$store.dispatch('UI/displayFreePlanLimitationMessages');
            //     }
            // });
        }

        if (!gpu.renderer.capabilities.isWebGL2) {
            this.$snackbar('Your browser doesn\'t seem to support WebGL2 which is required for fylm.ai. Please upgrade to a modern browser.', 'error', {
                class: 'browser-notice',
                timeout: -1
            });

            return;
        }

        if (detectSafari()) {
            if (Cookies.get('SafariPrivateModeInfoRead')) return;

            this.$snackbar(`Safari is not compatible with fylm.ai. Please use Chrome or Firefox.`, "error", {
                class: 'browser-notice',
                timeout: -1,
                clickHandler() {
                    Cookies.set('SafariPrivateModeInfoRead', true);
                }
            });
        }
    },

	computed: {
		...mapState("UI", ["UILoadedStatus"]),
		...mapState("Projects", ["projectSettings", "project"]),
		...mapState("Projects", {
			selectedProject: "modalSelectedProject",
		}),
		...mapState("User", ["account", "user", "currentPlan"]),
        ...mapGetters("User", ["isGuest"]),
		...mapGetters("Projects", ["projectsFeaturedImage", "isDefaultProjectImage"]),
		...mapGetters("Projects", {
			projects: "projectsFromActiveOrganisation",
		}),
        ...mapGetters('Notifications', ['notificationsOfNonActiveOrganization']),

        projectsListView() {
			if (!this.searchProject) return this.projects;
			else return this.projects.filter(project => project.name.toLowerCase().includes(this.searchProject.toLowerCase()));
		},
		filteredProjects() {
			if (!this.projects) return [];
			let projects = [...this.projects];
			if (this.searchProject) {
				projects = projects.filter(project => project.name.toLowerCase().includes(this.searchProject.toLowerCase()));
			}
			this.setFilteredProjectsLength(projects.length);
			projects = projects.splice((this.page - 1) * this.projectsPerPage, this.projectsPerPage);

			return projects;
		},
		placeholderProjects() {
			// adding placeholders
			let placeholderProjects = [];
			if (this.filteredProjects.length < this.projectsPerPage) {
				let projectsLength = this.filteredProjects.length;
				for (let i = 0; i < this.projectsPerPage - projectsLength; i++) {
					placeholderProjects.push({
						id: "placeholder-project-" + i,
						placeholder: true,
					});
				}
			}
			return placeholderProjects;
		},
		filteredProjectsWithPlaceholders() {
			return [...this.filteredProjects, ...this.placeholderProjects];
		},
		totalPages() {
			return Math.ceil(this.filteredProjectsLength / this.projectsPerPage);
			// else return Math.ceil(this.projects.length / this.projectsPerPage);
		},
		noAssignedProject() {
			if (!this.canCreateProject && this.projects.length == 0) return true;
			else return false;
		},
	},

	methods: {
		...mapActions("Helpers", ["revokeObjectURL"]),

		async openProject(toggleModal = true) {
			if (!this.selectedProject || this.loadingProject) return;
			this.loadingProject = this.selectedProject;

			// on first project load
			if (!this.project.id) {
				await this.loadScripts();
				this.$store.dispatch("App/setScriptsLoaded");
				this.$store.dispatch("App/setIDTList", window.config.IDT);
				this.$store.dispatch("App/setODTList", window.config.ODT);
			}

			if (this.$store.state.App.scriptsLoaded) {
                if (this.project.id) this.$store.dispatch("Projects/switchProject");
				await this.$store.dispatch("Projects/setProjectId", this.selectedProject);
				await this.$store.dispatch("Projects/selectProject");
				await this.$store.dispatch("Projects/refreshProjectTree", {
					createPresignedURL: true,
				});
				// this.$store.dispatch("Projects/openAllProjectTree");
				// this.$store.dispatch("Projects/fetchThumbnails");

                // only swith Matches on first image load
                if (!this.isGuest) {
                    window.lutify.switchMatches();
                }

                if (this.isGuest) {
                    this.$store.dispatch('Projects/openAllProjectTree');
                    this.$nextTick(() => {
                        $('#project-tree div[file] .file-name').first().click();
                    })
                } else {
                    const bins = vm.$store.getters['Projects/bins'];
                    if (bins.length > 0) {
                        vm.$store.dispatch("Projects/openProjectTree", [
                            { key: `${bins[0].id}-bin` }
                        ]);
					}
				}

                this.$store.dispatch('UserFiles/setLastStatePushInterval');

                this.$store.dispatch('Notifications/fetch');
                await this.$store.dispatch('Projects/subscribeToChannel');

                if (toggleModal) {
                    document.querySelector("#project-selector-modal").classList.toggle("hidden");
                    document.querySelector("#project-selector-modal").classList.toggle("fadeInDownModal");
                    document.querySelector("#app-view").classList.toggle("overlay");
                    window.lutify.activeModal = null;
                }

				this.loadingProject = false;
				this.listView = false;
				if (!this.$store.state.UI.UILoadedStatus) window.UI.startApp();
				window.UI.hideStartTips();
			}
		},

		createProject() {
			if (this.loadingProject) return;
			if (!this.canCreateProject) return;

			document.querySelector("#project-selector-modal").classList.toggle("hidden");
			document.querySelector("#project-selector-modal").classList.toggle("fadeInDownModal");
			document.querySelector("#modal-create-project").classList.toggle("hidden");
			document.querySelector("#modal-create-project").classList.toggle("fadeInDownModal");
			document.querySelector("#modal-create-project input").focus();

			this.listView = false;
		},

		openSettings(id) {
			this.$store.dispatch("Projects/selectProjectSettings", id);
		},

		onRename(project) {
			this.renamingProjectId = project.id;
			this.renamingProject = project.name;
		},

		onDelete() {
			document.querySelector("#project-selector-modal").classList.toggle("hidden");
			document.querySelector("#project-selector-modal").classList.toggle("fadeInDownModal");

			document.querySelector("#modal-delete-project").classList.toggle("hidden");
			document.querySelector("#modal-delete-project").classList.toggle("fadeInDownModal");
		},

		onAssignUsers() {
			document.querySelector("#project-selector-modal").classList.toggle("hidden");
			document.querySelector("#project-selector-modal").classList.toggle("fadeInDownModal");

			document.querySelector("#modal-assign-users").classList.toggle("invisible");
			document.querySelector("#modal-assign-users").classList.toggle("fadeInDownModal");

			if (this.$store.state.UI.UILoadedStatus) window.lutify.activeModal = document.querySelector("#modal-assign-users");
		},

		onShowMembers() {
			document.querySelector("#project-selector-modal").classList.toggle("hidden");
			document.querySelector("#project-selector-modal").classList.toggle("fadeInDownModal");

			document.querySelector("#modal-project-members").classList.toggle("hidden");
			document.querySelector("#modal-project-members").classList.toggle("fadeInDownModal");

			if (this.$store.state.UI.UILoadedStatus) window.lutify.activeModal = document.querySelector("#modal-project-members");
		},

		resetPage() {
			this.page = 1;
		},

		setFilteredProjectsLength(length) {
			this.filteredProjectsLength = length;
		},

		onPageChange() {
			this.$store.dispatch("Projects/setProjectsFeaturedImage", this.getProjectsThumbsView());
		},

		onScroll(event) {
			const element = event.currentTarget || event.target;
			let numberOfProjects = Math.ceil(element.scrollTop / 70) + 10;
			this.$store.dispatch("Projects/setProjectsFeaturedImage", this.getProjectsListView(numberOfProjects));
		},

		onProjectSelection(project_id) {
			this.$store.dispatch("Projects/setModalSelectedProject", project_id);
		},

		onProjectSelectionDoubleClick(project_id) {
			this.$store.dispatch("Projects/setModalSelectedProject", project_id);
			this.openProject();
		},

		async handleProjectRenaming(e) {
			if (e.key == "Enter" || e.keyCode == 13) {
				await this.$store.dispatch("Projects/updateProjectName", {
					project_id: this.renamingProjectId,
					name: this.renamingProject == "" ? `Project-${Date.now()}` : this.renamingProject,
				});
				await this.$store.dispatch("Projects/fetchProjects");
				if (this.$store.state.Projects.project.id) await this.$store.dispatch("Projects/selectProject");

				this.renamingProjectId = undefined;
			} else if (e.key == "Escape" || e.keyCode == 27) {
				this.renamingProjectId = undefined;
			}
		},

		cancelRenaming() {
			this.renamingProjectId = undefined;
		},

		getProjectsThumbsView() {
			return this.projects.filter(
				(project, index) => this.page * this.projectsPerPage - this.projectsPerPage <= index && index < this.page * this.projectsPerPage
			);
		},

		getProjectsListView(number) {
			return this.projects.filter((project, index) => index < number);
		},

        ownerRole(owner) {
		    let account = this.$store.getters['Organizations/memberAccountByUserId'](owner.id);
		    if (!account) return '';

		    return this.$store.getters['Roles/getRoleName'](account.pivot.role_id);
        }
	},
};
</script>

<style lang="scss">
#project-selector-modal {
	width: 50%;
	// max-height: 600px;

	.modal-header-container {
		display: flex;
		// justify-content: space-between;
		align-items: center;

		.organization-selector {
			display: flex;
			align-items: center;
            position: relative;
			.organization-selector--name {
				margin-bottom: 0;
				font-size: 1rem;
			}

			i {
				font-size: 18px;
			}

            &.--unread-notifications {
                &:before {
                    content: "";
                    display: block;
                    position: absolute;
                    top: -5px;
                    right: 7px;
                    width: 4px;
                    height: 4px;
                    background: var(--primary);
                    border-radius: 50%;
                }
            }
		}

        .notification-button-container {
            margin-left: 10px;
        }

		> .btn-close-modal {
			position: initial;
			margin-left: 20px;
		}

		.search-container {
			width: 200px;
			flex: initial;
			margin-left: auto;
			border: 1px solid var(--font-default-color);
			border-radius: 25px;
			.v-input__slot {
				padding: 0 12px 0 20px;
				background: transparent;
			}

			input,
			.v-label {
				font-size: 1rem;
				color: var(--font-default-color);
				font-weight: normal;
			}

			.v-icon,
			.v-icon:before {
				font-size: 18px;
				color: var(--font-default-color);
			}

			.v-input__control {
				min-height: initial;
			}
		}
	}

	.modal-tools-container {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-top: 30px;
		width: 100%;
		height: 25px;
		.view-buttons-coantainer {
			display: flex;
			align-items: center;
			i {
				cursor: pointer;
				&.--active {
					color: white;
				}
				& + i {
					margin-left: 15px;
				}
			}
		}
	}

	#projects-pagination {
		button {
			background: transparent;
			color: var(--font-default-color);
			border: none;
			box-shadow: none !important;
			&:hover {
				opacity: 1;
			}
			&.v-pagination__navigation {
				border: none;
				// margin: 0;
				margin-left: 0;
				margin-right: 0;
				width: 18px;
				height: 18px;
				i {
					color: var(--font-default-color);
					font-size: 18px;
					&:after {
						display: none;
					}
				}
			}
			&.v-pagination__item--active {
				// background-color: var(--font-default-color) !important;
				background-color: transparent !important;
				// border-color: var(--font-default-color) !important;
				color: var(--primary);
				font-weight: bold;
				box-shadow: none;
			}
			&.v-pagination__item {
				max-width: 18px;
				min-width: 18px;
				height: 18px;
				font-size: 0.9rem;
				// margin-top: 0;
				// margin-bottom: 0;
			}
		}
	}

	.modal-body-container {
		// overflow-y: scroll;
		height: 100%;

		&.--no-projects-assigned {
			display: flex;
			flex-direction: column;
			justify-content: center;
			align-items: center;
			min-height: 570px;

			i {
				font-size: 4rem;
				margin-bottom: 15px;
			}

			p {
				line-height: 1.6rem;
				text-align: center;
				font-weight: 400;
			}
		}

		.spinner-container {
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			z-index: 10;
		}
	}

	.projects-container {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		grid-column-gap: 35px;
		grid-row-gap: 35px;
		margin: 10px 0 25px;
	  	height: 65vh;
		width: 100%;
		min-width: 100%;
		transition: none;
		.project {
			position: relative;
			display: grid;
			align-items: center;
			grid-template-columns: 1fr 22px;
		  	grid-template-rows: 1fr auto;
			grid-row-gap: 4px;
			grid-column-gap: 10px;
			grid-template-areas: "img img" "title options";
			cursor: pointer;
			// padding: 10px;
			&.--selected {
				.project-image-container {
					&:after {
						display: block;
						content: "";
						position: absolute;
						top: 0;
						left: 0;
						width: 100%;
						height: 100%;
						border: 3px solid var(--primary);
						border-radius: var(--thumbnail-lg-border-radius);
					}
					&.--default:before {
						border-color: transparent;
					}
				}

				> .project-name {
					font-weight: bold;
				}
			}

			&.--loading {
				// border: 3px solid rgba(0, 0, 0, 0.5);
				cursor: not-allowed;

				.project-image-container::after {
					content: "";
					background: rgba(0, 0, 0, 0.5);
					z-index: 5;
					width: 100%;
					height: 100%;
					display: block;
					position: absolute;
					top: 0;
					left: 0;
					border-radius: var(--thumbnail-lg-border-radius);
				}

				.project-image.--default::after {
					width: 100%;
					left: 0;
				}

				.project-name,
				.project-options-btn {
					opacity: 0.5;
					cursor: not-allowed;
				}
			}

			&.--placeholder {
				visibility: hidden;
			}

			> .project-image-container {
				position: relative;
				grid-area: img;
				height: 100%;

				&.--default {
					// background-size: contain;
					&:before {
						content: "";
						position: absolute;
						top: 0;
						left: 0;
						width: 100%;
						height: 100%;
						border: 1px solid var(--font-default-color);
						border-radius: var(--thumbnail-lg-border-radius);
					}
				}
			}

			.project-image {
			  position: absolute;
			  top: 0;
			  left: 0;
              width: 100%;
			  height: 100%;
              // border: 3px solid transparent;
				border-radius: var(--thumbnail-lg-border-radius);
				object-fit: cover;
				//height: 160px;
				// height: 100%;
				// max-height: 56.5%;
				// padding-top: 56.5%;
				// background-size: cover;
				// background-position: center;
				//position: relative;
			}

			.project-owner-avatar {
				position: absolute;
				right: 7px;
				bottom: 7px;
				border-radius: 50%;
				width: 30px;
				height: 30px;
				border: 1px solid var(--primary);
				background: white;
			}

			.project-name {
				line-height: 22px;
				height: 22px;
				text-overflow: ellipsis;
				overflow: hidden;
				white-space: nowrap;
				font-weight: 500;
			}

			.project-options-btn {
				cursor: pointer;
				font-size: 18px;
				color: var(--font-default-color);
				&:hover {
					color: var(--primary);
				}
			}

			.v-input {
				font-size: 14px;
				.v-input__control {
					min-height: initial;
					// height: 10px;
					input {
						line-height: 1;
						color: var(--font-default-color);
						padding: 0;
						text-overflow: ellipsis;
						overflow: hidden;
						white-space: nowrap;
					}

					.v-input__slot {
						padding: 0;
						background: transparent;
					}
				}

				&--is-focused {
					.v-input__slot {
						// background: #363636;
						background: transparent;
					}
				}
			}
		}
	}

	.projects-container--list-view {
		margin: 10px -25px 25px;
		// height: 400px;
		width: 100%;
		min-width: 100%;
		transition: none;

		.project {
			padding: 10px 25px;
			display: grid;
			grid-template-columns: 75px 1fr 30% 1fr 18px;
			grid-column-gap: 15px;
			align-items: center;

            &.--is-guest {
                grid-template-columns: 75px 1fr 30% 1fr;
            }

			&.--selected,
			&:hover {
				background: #363636;
			}
			&.--loading {
				opacity: 0.5;
				cursor: not-allowed;
				&:hover {
					background: transparent;
				}
				.project-options-btn {
					cursor: not-allowed;
				}
			}

			.project-image-container {
				position: relative;
			}

			.project-image {
				width: 100%;
				height: 42px;
				// height: 56.5%;
				object-fit: cover;
				border: 1px solid transparent;
				border-radius: var(--thumbnail-sm-border-radius);
				&.--default {
					border-color: var(--font-default-color);
				}
			}

			.project-owner-container {
				display: flex;
				align-items: center;

				> .project-owner-avatar {
					border-radius: 50%;
					width: 30px;
					height: 30px;
					border: 1px solid var(--primary);
					margin-right: 10px;
					background: white;
				}
			}

			.project-name,
			.project-owner-name,
			.project-date {
				text-overflow: ellipsis;
				overflow: hidden;
				white-space: nowrap;
			}

			.v-input {
				font-size: 14px;
				.v-input__control {
					min-height: initial;
					// height: 10px;
					input {
						line-height: 1;
						color: var(--font-default-color);
						padding: 2px 0;
						text-overflow: ellipsis;
						overflow: hidden;
						white-space: nowrap;
					}

					.v-input__slot {
						padding: 0;
						background: transparent;
					}
				}

				&--is-focused {
					.v-input__slot {
						background: transparent;
					}
				}
			}
		}
	}

	.buttons-continer {
		margin-top: 35px !important;
		button {
			background: transparent !important;
			border: 1px solid var(--font-default-color);
			color: var(--font-default-color) !important;
			&:hover {
				color: white;
				border-color: white;
				span {
					color: white;
				}
			}
			> span {
				color: var(--font-default-color);
			}

			&[disabled]:hover {
				border: 1px solid var(--font-default-color);
				color: var(--font-default-color);
				span {
					color: var(--font-default-color);
				}
			}
		}
	}
}

.project-options-container {
	border-radius: var(--box-rounding);
	.v-list {
		background: var(--dark-gray);
		padding: 25px 0;
		.v-list-item {
			min-height: 20px;
			padding: 0 25px;
			cursor: pointer;
			&:hover {
				background: #363636;
			}
			&--disabled {
				opacity: 0.6;
			}

			.option {
				color: var(--font-default-color);
				font-size: 1rem;
			}
		}
	}
}

.account-menu--theme {
	&.--projects {
		.account-menu-content {
			> span {
				padding: 25px 25px 0;
				display: block;
				line-height: 2;
				font-weight: bold;
			}
			#account-switch-container {
				// padding: 25px 0 0;
			}
		}
	}
}
</style>
